<script setup lang="ts">
import {
    messageAlertSelectProductHappy,
    messageAlertSelectProductHappyEmpty
} from '@/happyTheme/event/product/messageAlertSelectProductHappy'
import { nextTick, onMounted, ref, watch } from 'vue'

const hasScroll = ref(false)
const contentRef = ref<HTMLElement | null>(null)
const isScrolledToBottom = ref(false)
const showScrollArrow = ref(false)

const props = defineProps<{
    option: any[]
    type: string
    message: string
    title: string
}>()

function checkScrollStatus() {
    if (contentRef.value) {
        const element = contentRef.value

        hasScroll.value = contentRef.value.scrollHeight > contentRef.value.clientHeight

        const scrollPosition = element.scrollTop + element.clientHeight
        const scrollHeight = element.scrollHeight
        isScrolledToBottom.value = Math.abs(scrollHeight - scrollPosition) <= 5

        showScrollArrow.value = hasScroll.value && !isScrolledToBottom.value
    }
}

function closeMessageAlertProductHappy() {
    messageAlertSelectProductHappy.value = messageAlertSelectProductHappyEmpty
}

function scrollToBottom() {
    if (contentRef.value) {
        contentRef.value.scrollTo({
            top: contentRef.value.scrollHeight,
            behavior: 'smooth'
        })
    }
}

watch(
    () => props.option,
    () => {
        nextTick(() => {
            checkScrollStatus()
        })
    },
    { deep: true }
)

onMounted(() => {
    checkScrollStatus()

    contentRef.value?.addEventListener('scroll', checkScrollStatus)
})
</script>

<template>
    <transition name="slide-alert" mode="out-in">
        <section
            class="section-message-alert-selected-product-happy"
            v-if="props.type.length !== 0"
        >
            <section
                class="section-message-alert-selected-product-happy-overlay"
                @click="closeMessageAlertProductHappy()"
            ></section>

            <section class="section-message-alert-product-show-happy" :class="props.type">
                <button
                    class="close-modal-product-alert-happy"
                    :class="{
                        'bg-header-red-happy': props.type === 'error',
                        'bg-mouse-enter-yellow-happy-light': props.type === 'warning',
                        'bg-green-light-happy': props.type === 'success'
                    }"
                >
                    <h3
                        class="h3-title-alert-product-happy"
                        :class="{
                            'text-bg-happy-light': props.type === 'error',
                            'text-text-dark-happy-light':
                                props.type === 'warning' || props.type === 'success'
                        }"
                    >
                        {{ props.title }}
                    </h3>
                    <svg
                        @click="closeMessageAlertProductHappy()"
                        class="svg-cross-product-alert-happy"
                        :class="{
                            'stroke-bg-happy-light hover:stroke-text-dark-happy-light active:stroke-text-dark-happy-light':
                                props.type === 'error',
                            'stroke-text-dark-happy-light hover:stroke-bg-happy-light active:stroke-bg-happy-light':
                                props.type === 'warning' || props.type === 'success'
                        }"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 4 22 22"
                    >
                        <path
                            d="M16 8L8 16M8.00001 8L16 16"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                    </svg>
                </button>
                <section
                    v-if="props.option.length !== 0"
                    class="section-message-alert-product-content-happy"
                    ref="contentRef"
                    :class="{
                        'text-text-dark-happy-light': props.type === 'warning',
                        'text-bg-happy-light': props.type !== 'warnings'
                    }"
                >
                    <div v-for="(alert, i) in props.option" class="">
                        <p class="option-name-alert-product-show-happy">
                            {{ alert.option_name + ' :' }}
                        </p>
                        <p
                            class="px-3"
                            :class="{
                                'border-b': i !== props.option.length - 1,
                                'border-text-dark-happy-light ': props.type === 'warning',
                                'pb-0':
                                    props.type === 'warning' &&
                                    i === props.option.length - 1,
                                'pb-2':
                                    props.type === 'error' ||
                                    (props.type === 'warning' &&
                                        i !== props.option.length - 1)
                            }"
                        >
                            {{ alert.message }}
                        </p>
                    </div>
                    <div
                        v-if="props.type === 'warning'"
                        class=""
                        :class="{
                            'bg-mouse-enter-yellow-happy-light rounded-b-xl':
                                props.type === 'warning'
                        }"
                    >
                        <p class="alert-selected-continue-happy">
                            Vous pouvez cliquer a nouveau si vous ne souhaiter pas d'autre
                            options !
                        </p>
                    </div>
                </section>
                <div
                    v-if="showScrollArrow"
                    class="has-scroll-div-happy"
                    @click="scrollToBottom()"
                >
                    <svg
                        class="svg-arrow-back-alert-happy"
                        viewBox="0 0 384 217"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M7.32225 7.65667C17.0853 -2.10666 32.9146 -2.10666 42.6776 7.65667L191.667 156.643L340.657 7.65667C350.42 -2.10666 366.247 -2.10666 376.01 7.65667C385.773 17.42 385.773 33.2467 376.01 43.01L209.343 209.678C204.657 214.366 198.297 217 191.667 217C185.037 217 178.677 214.366 173.99 209.678L7.32225 43.01C-2.44075 33.2467 -2.44075 17.42 7.32225 7.65667Z"
                        />
                    </svg>
                </div>

                <section
                    v-if="props.type === 'success'"
                    class="section-message-success-product-content-happy"
                >
                    <p>{{ props.message }}</p>
                </section>
            </section>
        </section>
    </transition>
</template>

<style scoped>
.section-message-alert-selected-product-happy {
    @apply fixed h-screen min-w-full z-30;
}
.section-message-alert-selected-product-happy-overlay {
    @apply h-full;
}

.section-message-alert-product-show-happy {
    @apply fixed top-0 z-30 mt-36 flex flex-col justify-center max-h-[80vh] rounded-xl left-1/2 transform -translate-x-1/2;
    @apply max-w-[90vw] w-auto min-w-[300px];
}
.error {
    @apply bg-border-red-happy-light;
}

.warning {
    @apply bg-button-yellow-happy-light;
}

.close-modal-product-alert-happy {
    @apply w-full flex justify-between pt-2 pr-2 pl-3 items-center  rounded-t-xl;
}

.h3-title-alert-product-happy {
    @apply font-semibold;
}

.svg-cross-product-alert-happy {
    @apply w-9 h-9;
}

.svg-cross-product-alert-happy:active,
.svg-cross-product-alert-happy:focus {
    @apply stroke-text-dark-happy-light;
}

.has-scroll-div-happy {
    @apply absolute bottom-3 left-1/2 transform -translate-x-1/2 bg-text-dark-happy-light opacity-80 min-w-10 h-10 rounded-full;
}

.has-scroll-div-happy:active,
.has-scroll-div-happy:focus {
    @apply opacity-100;
}

.svg-arrow-back-alert-happy {
    @apply absolute max-w-10 max-h-10 left-1/2 transform -translate-x-1/2 bottom-3 fill-bg-happy-light;
}

.section-message-alert-product-content-happy {
    @apply flex flex-col gap-2 pt-2 overflow-y-auto;
}

.option-name-alert-product-show-happy {
    @apply font-semibold px-3;
}

.alert-selected-continue-happy {
    @apply px-3 font-bold pb-3 pt-3 text-center;
}

.section-message-success-product-content-happy {
    @apply bg-green-happy text-text-dark-happy-light font-semibold text-center p-4 rounded-b-xl;
}

.slide-alert-enter-active {
    transition:
        transform 0.4s cubic-bezier(0.4, 0, 0.2, 1),
        opacity 0.4s;
}
.slide-alert-leave-active {
    transition:
        transform 0.4s cubic-bezier(0.4, 0, 0.2, 1),
        opacity 0.4s;
}
.slide-alert-enter-from {
    transform: translateX(100vw);
    opacity: 0;
}
.slide-alert-enter-to {
    transform: translateX(0);
    opacity: 1;
}
.slide-alert-leave-from {
    transform: translateX(0);
    opacity: 1;
}
.slide-alert-leave-to {
    transform: translateX(-100vw);
    opacity: 0;
}

@media (hover: hover) {
    .svg-cross-product-alert-happy:hover {
        @apply stroke-text-dark-happy-light;
    }
    .has-scroll-div-happy:hover {
        @apply opacity-100;
    }
}
</style>
